import React, { useState, useMemo } from 'react'

import { LinkOutlined, CheckOutlined, CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { useOperatorData } from '@contexts/operator/OperatorDataContext'
import useBatchOperation from '@hooks/shared/useBatchOperation'
import { useImport } from '@hooks/shared/useImport'
import ImportModal from '@shared/ImportModal'
import useCan from '@shared/permissions/useCan'
import { MultipleValue, SelectValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import { Tag, Space, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { downloadImportTemplate } from '@utils/downloadTemplate'
import { formatDateTime } from '@utils/dateTime'

import type { Permission } from '@/permissions_manifest.generated'


interface BundleItem {
  product_sku: string;
  product_name: string;
  quantity: number;
  available_quantity: number;
}

interface BundleItemsCellProps {
  bundleItems: BundleItem[] | null | undefined;
}

const BundleItemsCell: React.FC<BundleItemsCellProps> = ({ bundleItems }) => {
  if (!bundleItems || bundleItems.length === 0) {
    return <span className="text-muted">-</span>
  }
  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {bundleItems.map((item, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '2px 0'
          }}
        >
          <Tooltip title={`SKU: ${item.product_sku}`}>
            <span style={{
              fontSize: '12px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              marginRight: '8px'
            }}>
              {item.product_name}
            </span>
          </Tooltip>
          <Space size={4}>
            <Tag color="blue" style={{ margin: 0 }}>
              x{item.quantity}
            </Tag>
            <Tooltip title={`可用庫存: ${item.available_quantity}`}>
              <Tag
                color={item.available_quantity >= item.quantity ? 'success' : 'error'}
                style={{ margin: 0 }}
              >
                {item.available_quantity}
              </Tag>
            </Tooltip>
          </Space>
        </div>
      ))}
    </Space>
  )
}

interface Merchant {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface BundleService {
  export: (ids: string[], language: string) => Promise<unknown>;
  import: (formData: FormData) => Promise<unknown>;
  [key: string]: any;
}

interface BundlesListViewProps {
  role?: 'admin' | 'operator';
  tableId?: string;
  service: BundleService;
  addJob?: (jobId: string, jobName: string) => void;
  importModalVisible?: boolean;
  setImportModalVisible?: (visible: boolean) => void;
}

interface BatchAction {
  name: string;
  className?: string;
  requiredPermission?: Permission;
  do: (selected: Iterable<string>) => void;
}

const BundlesListView: React.FC<BundlesListViewProps> = ({
  role = 'admin',
  tableId = 'BundlesTable',
  service, // New prop
  addJob,
  importModalVisible: externalImportModalVisible,
  setImportModalVisible: externalSetImportModalVisible
}) => {
  // 內部 .jsx 子元件型別不完整（context-derived props 不會出現在頂層 destructure），
  // 因此一律 cast 成 ComponentType<any> 保留原 runtime 行為。
  // 放在 component body 內以避免 Vite HMR 下的 top-level TDZ。

  const { t, i18n } = useTranslation()
  const can = useCan()
  const [lastModified, setLastModified] = useState(0)
  const [internalImportModalVisible, setInternalImportModalVisible] = useState(false)

  // Support external or internal import modal control
  const importModalVisible = externalImportModalVisible !== undefined ? externalImportModalVisible : internalImportModalVisible
  const setImportModalVisible = externalSetImportModalVisible || setInternalImportModalVisible

  // Get merchants from OperatorDataContext (only for operator role)
  // Always call hooks unconditionally
  const operatorData = useOperatorData() as { merchants?: Merchant[] } | null
  const merchants: Merchant[] = role === 'operator' ? (operatorData?.merchants || []) : []

  // 使用共用 batch operation hook
  const executeBatchOperation = (useBatchOperation as any)(service, addJob)

  // 匯入功能（使用 Context 共享背景任務管理器）
  const handleImport = (useImport as any)({
    action: (formData: FormData) => service.import(formData),
    addJob,
    jobName: 'shared.bundles.list.import.job_name',
    i18nKeys: {
      noFile: 'shared.bundles.list.import.no_file',
      invalidFile: 'shared.bundles.list.import.invalid_file',
      success: 'shared.bundles.list.import.success',
      error: 'shared.bundles.list.import.error',
    }
    // 注意：不在 onSuccess 重新載入表格，因為 Background Job 還在執行中
    // 使用者需要在 Background Job 完成後手動重新整理，或等待自動通知
  })

  // 處理 Modal 匯入
  const handleModalImport = async (file: File, merchantId: string, overrideExisting: boolean) => {
    await handleImport(file, merchantId, overrideExisting)
  }

  // 處理下載範例
  const handleDownloadTemplate = async (merchantId?: string) => {
    await (downloadImportTemplate as any)({
      service,
      language: i18n.language,
      merchantId,
      t,
      successKey: 'shared.bundles.list.template.success',
      errorKey: 'shared.bundles.list.template.error'
    })
  }
  const columns = useMemo(() => {
    const baseColumns: any[] = [
      {
        nameKey: `models.bundle.bundle_name`,
        persist: true,
        sortable: true,
        sortFieldName: 'bundles.name',
        width: '200px',
        wrap: true,
        cell: ({ id, name }: { id: string; name: string }) => {
          const urlPrefix = role === 'operator' ? '/operator' : '/admin'
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Link to={`/bundles/${id}`} style={{ wordBreak: 'break-word' }}>
                {name}
              </Link>
              <a
                href={`${urlPrefix}/bundles/${id}`}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
                style={{ flexShrink: 0 }} rel="noreferrer"
              >
                <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
              </a>
            </div>
          )
        },
      },
      {
        nameKey: `models.bundle.sku`,
        sortable: true,
        sortFieldName: 'bundles.sku',
        wrap: true,
        selector: ({ sku }: { sku: string }) => sku,
      },
      {
        nameKey: `models.bundle.barcode`,
        sortable: true,
        sortFieldName: 'bundles.barcode',
        wrap: true,
        selector: ({ barcode }: { barcode: string }) => barcode,
      },
      {
        nameKey: 'models.bundle.available_set_quantity',
        name: (
          <Space size={4}>
            {t('models.bundle.available_set_quantity')}
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('models.bundle.available_set_quantity_tooltip')}</span>}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: '120px',
        wrap: true,
        cell: ({ available_set_quantity }: { available_set_quantity: number }) => (
          <Tag color={available_set_quantity > 0 ? 'success' : 'default'} style={{ fontSize: '14px' }}>
            {available_set_quantity} 套
          </Tag>
        ),
      },
      {
        nameKey: 'models.bundle.bundle_items',
        name: (
          <Space size={4}>
            {t('models.bundle.bundle_items')}
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('models.bundle.bundle_items_tooltip')}</span>}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: '350px',
        wrap: true,
        cell: ({ bundle_items }: { bundle_items: BundleItem[] | null | undefined }) => <BundleItemsCell bundleItems={bundle_items} />,
      },
      {
        nameKey: `common.created_at`,
        sortable: true,
        sortFieldName: 'bundles.created_at',
        wrap: true,
        cell: ({ created_at }: { created_at: string | null }) => <>{formatDateTime(created_at)}</>,
      },
      {
        nameKey: 'models.bundle.activated',
        name: (
          <Space size={4}>
            {t('models.bundle.activated')}
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('models.bundle.activated_tooltip')}</span>}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        width: "80px",
        persist: true,
        cell: ({ activated }: { activated: boolean }) => {
          if (activated) {
            return <CheckOutlined style={{ color: 'green' }} />
          }
          return <CloseOutlined style={{ color: '#999' }} />
        }
      }
    ]
    // Operator: 在 SKU 後面添加 Merchant 欄位
    if (role === 'operator' && merchants && merchants.length > 0) {
      baseColumns.splice(2, 0, {
        nameKey: `models.bundle.merchant`,
        wrap: true,
        selector: ({ merchant_id }: { merchant_id: string }) => {
          return merchants.find((merchant) => merchant.id === merchant_id)?.name
        },
      })
    }
    return baseColumns
  }, [role, merchants])
  const supportRules = useMemo(() => {
    const baseRules: any[] = [
      {
        label: t(`models.bundle.sku`),
        component: MultipleValue,
        inputName: 'bundles.sku',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.bundles.list.filter_tooltips.sku.title'),
          content: t('shared.bundles.list.filter_tooltips.sku.content'),
        },
      },
      {
        label: t(`models.bundle.barcode`),
        component: MultipleValue,
        inputName: 'bundles.barcode',
        createable: true,
        tooltip: {
          title: t('shared.bundles.list.filter_tooltips.barcode.title'),
          content: t('shared.bundles.list.filter_tooltips.barcode.content'),
        },
      },
      {
        label: t(`models.bundle.bundle_name`),
        component: MultipleValue,
        inputName: 'bundles.name',
        createable: true,
        tooltip: {
          title: t('shared.bundles.list.filter_tooltips.bundle_name.title'),
          content: t('shared.bundles.list.filter_tooltips.bundle_name.content'),
        },
      },
      {
        label: t(`models.bundle.activated`),
        component: SelectValue,
        inputName: 'bundles.activated',
        defaultOptions: [
          {
            label: t(`common.enabled`),
            value: 'true',
          },
          {
            label: t(`common.disabled`),
            value: 'false',
          },
        ],
        tooltip: {
          title: t('shared.bundles.list.filter_tooltips.activated.title'),
          content: t('shared.bundles.list.filter_tooltips.activated.content'),
        },
      }
    ]
    // Operator: 添加 Merchant 篩選器
    if (role === 'operator' && merchants && merchants.length > 0) {
      baseRules.splice(2, 0, {
        label: t(`models.bundle.merchant`),
        component: MultipleValue,
        inputName: 'bundles.merchant_id',
        defaultOptions: merchants.map((merchant) => ({
          label: merchant.name,
          value: merchant.id,
        })),
        tooltip: {
          title: t('shared.bundles.list.filter_tooltips.merchant.title'),
          content: t('shared.bundles.list.filter_tooltips.merchant.content'),
        },
      })
    }
    return baseRules
  }, [t, role, merchants])
  const actions = useMemo<BatchAction[]>(() => ([
    {
      name: `common.button.export`,
      className: 'btn btn-primary btn-outline mb-0',
      requiredPermission: 'products:export',
      do: (selected) => executeBatchOperation({
        task: () => service.export(Array.from(selected), i18n.language),
        jobName: 'common.button.export'
      })
    }
  ] satisfies BatchAction[]).filter(action => !action.requiredPermission || can(action.requiredPermission)),
  [executeBatchOperation, service, i18n, can])
  return (
    <>
      <TableIndex
        columns={columns}
        searchable={{
          match: 'prefix',
          searchFields: [
            t('models.bundle.sku'),
            t('models.bundle.bundle_name'),
            t('models.bundle.barcode'),
          ],
        }}
        columnsEditable={true}
        tableId={tableId}
        defaultQuery={{
          sorting_field: 'bundles.id',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        actions={actions}
        service={service}
        filterHelpExample={t('shared.bundles.list.filter_help_example')}
        filterDocsUrl={`/docs/${role}/bundles/list#features`}
      />

      {/* 匯入 Modal */}
      <ImportModal
        open={importModalVisible}
        onClose={() => setImportModalVisible(false)}
        onImport={handleModalImport}
        onDownloadTemplate={handleDownloadTemplate}
        role={role}
        merchants={merchants}
        title={t('shared.bundles.list.import.modal_title')}
      />
    </>
  )
}

export default BundlesListView;
